import { getFontFamilyFromAssetMeta } from "@runcheetah/shared";

export type AssetGalleryFilter = "images" | "referentiable" | "fonts";
export type AssetGallerySourceGroup = "instagram" | "website" | "fonts";

export type AssetGalleryFilterInput = {
  assetKind: "image" | "font";
  imageClassification: string | null;
  isVector: boolean;
  width: number | null;
  height: number | null;
};

export type AssetGallerySourceGroupInput = AssetGalleryFilterInput & {
  sourceTypes: Array<"website" | "instagram">;
};

export type AssetDisplayInput = {
  id: string;
  canonicalAssetId: string | null;
  sourceUrl: string;
  pageUrl: string | null;
  sourceType: "website" | "instagram";
  instagramCaption: string | null;
  referenceEnabled: boolean;
};

export type DisplayAssetFields<T extends AssetDisplayInput> = T & {
  occurrenceCount: number;
  sourceUrls: string[];
  pageUrls: string[];
  sourceTypes: Array<T["sourceType"]>;
  instagramCaptions: string[];
};

export const isReferentiableImageAsset = (asset: AssetGalleryFilterInput): boolean => {
  return (
    asset.assetKind === "image" &&
    (asset.imageClassification === "pure_photo" ||
      asset.imageClassification === "photo_with_graphics_or_text") &&
    !asset.isVector &&
    asset.width !== null &&
    asset.height !== null &&
    asset.width >= 512 &&
    asset.height >= 512
  );
};

export const filterAssetsForGallery = <T extends AssetGalleryFilterInput>(
  assets: T[],
  filter: AssetGalleryFilter,
): T[] => {
  if (filter === "fonts") {
    return assets.filter((asset) => asset.assetKind === "font");
  }

  if (filter === "referentiable") {
    return assets.filter(isReferentiableImageAsset);
  }

  return assets.filter((asset) => asset.assetKind === "image");
};

export const getAssetGallerySourceGroup = (
  asset: AssetGallerySourceGroupInput,
): AssetGallerySourceGroup => {
  if (asset.assetKind === "font") {
    return "fonts";
  }

  return asset.sourceTypes.includes("instagram") ? "instagram" : "website";
};

const ASSET_GALLERY_SOURCE_GROUP_META: Record<
  AssetGallerySourceGroup,
  { title: string; description: string }
> = {
  instagram: {
    title: "Instagram assets",
    description: "Images imported from connected Instagram accounts.",
  },
  website: {
    title: "Website assets",
    description: "Images discovered while crawling the brand website.",
  },
  fonts: {
    title: "Fonts",
    description: "Type assets discovered during ingestion.",
  },
};

const ASSET_GALLERY_SOURCE_GROUP_ORDER: AssetGallerySourceGroup[] = [
  "instagram",
  "website",
  "fonts",
];

export const groupAssetsBySource = <T extends AssetGallerySourceGroupInput>(assets: T[]) =>
  ASSET_GALLERY_SOURCE_GROUP_ORDER.map((key) => ({
    key,
    ...ASSET_GALLERY_SOURCE_GROUP_META[key],
    assets: assets.filter((asset) => getAssetGallerySourceGroup(asset) === key),
  })).filter((group) => group.assets.length > 0);

export const formatBytes = (bytes: number | null): string => {
  if (bytes === null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export const formatImageClassificationLabel = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

export const normalizeSourceUrl = (value: string): string => {
  try {
    const parsed = new URL(value);
    parsed.hash = "";
    return parsed.toString();
  } catch {
    return value;
  }
};

export const getAssetFontFamily = (asset: {
  metaJson: unknown;
  assetKind: "image" | "font";
}): string | null => {
  return getFontFamilyFromAssetMeta(asset.metaJson, asset.assetKind)?.display ?? null;
};

export const getAssetNormalizedFontFamily = (asset: {
  metaJson: unknown;
  assetKind: "image" | "font";
}): string | null => {
  return getFontFamilyFromAssetMeta(asset.metaJson, asset.assetKind)?.normalized ?? null;
};

export const getAssetFontDisplayName = (asset: {
  metaJson: unknown;
  assetKind: "image" | "font";
  sourceUrl: string;
}): string | null => {
  const family = getAssetFontFamily(asset);
  if (family) {
    return family;
  }

  try {
    const path = new URL(asset.sourceUrl).pathname;
    const fileName = path.split("/").filter(Boolean).pop();
    return fileName ?? null;
  } catch {
    const fileName = asset.sourceUrl.split("/").filter(Boolean).pop();
    return fileName ?? null;
  }
};

export const parseLegacyTypographyFamilyNames = (value: string): string[] =>
  value
    .split("|")
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .map((part) => {
      const matched = part.match(/^(.+?)\s+\((Headings|Body|Mixed)\)/i);
      if (matched?.[1]) {
        return matched[1].trim();
      }

      return part.split(" - weights:")[0]?.split(" - ")[0]?.trim() ?? "";
    })
    .filter((family) => family.length > 0);

export const buildDisplayAssets = <T extends AssetDisplayInput>(
  assets: T[],
): Array<DisplayAssetFields<T>> => {
  const grouped = new Map<string, DisplayAssetFields<T>>();

  for (const asset of assets) {
    const key = asset.canonicalAssetId ?? asset.id;
    const existing = grouped.get(key);

    if (!existing) {
      grouped.set(key, {
        ...asset,
        occurrenceCount: 1,
        sourceUrls: [asset.sourceUrl],
        pageUrls: asset.pageUrl ? [asset.pageUrl] : [],
        sourceTypes: [asset.sourceType],
        instagramCaptions: asset.instagramCaption ? [asset.instagramCaption] : [],
      });
      continue;
    }

    existing.occurrenceCount += 1;
    if (!existing.sourceUrls.includes(asset.sourceUrl)) {
      existing.sourceUrls.push(asset.sourceUrl);
    }
    if (asset.pageUrl && !existing.pageUrls.includes(asset.pageUrl)) {
      existing.pageUrls.push(asset.pageUrl);
    }
    if (!existing.sourceTypes.includes(asset.sourceType)) {
      existing.sourceTypes.push(asset.sourceType);
    }
    if (asset.instagramCaption && !existing.instagramCaptions.includes(asset.instagramCaption)) {
      existing.instagramCaptions.push(asset.instagramCaption);
    }
    existing.referenceEnabled ||= asset.referenceEnabled;
  }

  return [...grouped.values()];
};
